import { ApiEndpoint } from '../../../../types/admin';
import { HubSpotDealStage } from '../../../../types/hubspot-enums';

// Define all available API endpoints grouped by category
export const API_ENDPOINTS_GROUPED: Record<string, ApiEndpoint[]> = {
  general: [
    {
      id: 'me',
      name: 'Get Current User',
      method: 'GET',
      path: '/api/general/me',
      description: 'Returns information about the current authenticated user',
      enabled: true,
    },
    {
      id: 'status',
      name: 'API Status',
      method: 'GET',
      path: '/api/general/status',
      description: 'Returns the API status and health information (no auth required)',
      enabled: true,
    },
  ],
  marketcompass: [
    {
      id: 'accessible-markets',
      name: 'Accessible Markets',
      method: 'GET',
      path: '/api/marketcompass/markets/accessible',
      description: 'Returns markets accessible to the current user',
      enabled: true,
    },
    {
      id: 'check-single-market-access',
      name: 'Check Single Market Access',
      method: 'GET',
      path: '/api/marketcompass/markets/{marketId}/check-access',
      description: 'Check if user has access to a specific market',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market to check access for',
          placeholder: 'e.g., 123'
        }
      ]
    },
    {
      id: 'market-access',
      name: 'Check Market Access (Bulk)',
      method: 'POST',
      path: '/api/marketcompass/subscriptions/check-access',
      description: 'Checks user access to multiple markets using POST body',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        marketIds: [1, 2, 3]
      }, null, 2)
    },
    {
      id: 'user-entitlements',
      name: 'User Entitlements',
      method: 'GET',
      path: '/api/marketcompass/subscriptions/my-entitlements',
      description: 'Returns user subscription entitlements with optional filters',
      enabled: true,
      queryParams: [
        {
          name: 'status',
          type: 'string',
          required: false,
          description: 'Filter by status: active, paused, expired',
          placeholder: 'e.g., active'
        },
        {
          name: 'accessLevel',
          type: 'string',
          required: false,
          description: 'Filter by access level: market, country, continent',
          placeholder: 'e.g., market'
        },
        {
          name: 'includeExpired',
          type: 'boolean',
          required: false,
          description: 'Include expired entitlements',
          placeholder: 'true or false'
        }
      ]
    },
    {
      id: 'market-reports',
      name: 'Get Market Reports',
      method: 'GET',
      path: '/api/marketcompass/markets/{marketId}/reports',
      description: 'Returns a list of available reports for a specific market',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market to get reports for',
          placeholder: 'e.g., 123'
        }
      ]
    },
    {
      id: 'report-download-url',
      name: 'Get Report Download URL',
      method: 'GET',
      path: '/api/marketcompass/markets/{marketId}/reports/{reportId}/download-url',
      description: 'Generates a short-lived SAS URL for downloading a specific report',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market',
          placeholder: 'e.g., 123'
        },
        {
          name: 'reportId',
          type: 'string',
          required: true,
          description: 'ID of the report to download',
          placeholder: 'e.g., 456'
        }
      ]
    },
    {
      id: 'market-podcasts',
      name: 'Get Market Podcasts',
      method: 'GET',
      path: '/api/marketcompass/markets/{marketId}/podcasts',
      description: 'Returns a list of available podcasts for a specific market',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market to get podcasts for',
          placeholder: 'e.g., 123'
        }
      ]
    },
    {
      id: 'podcast-stream-url',
      name: 'Get Podcast Stream URL',
      method: 'GET',
      path: '/api/marketcompass/markets/{marketId}/podcasts/{podcastId}/stream-url',
      description: 'Generates a short-lived SAS URL for streaming a podcast',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market',
          placeholder: 'e.g., 123'
        },
        {
          name: 'podcastId',
          type: 'string',
          required: true,
          description: 'ID of the podcast to stream',
          placeholder: 'e.g., 789'
        }
      ]
    },
    {
      id: 'recent-reports',
      name: 'Get Recent Reports',
      method: 'GET',
      path: '/api/marketcompass/reports/recent',
      description: 'Returns recent reports across all markets the user has access to',
      enabled: true,
      queryParams: [
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Maximum number of reports to return (default: 10, max: 50)',
          placeholder: 'e.g., 20'
        }
      ]
    },
  ],
  store: [
    {
      id: 'customer-portal',
      name: 'Customer Portal',
      method: 'POST',
      path: '/api/store/customerPortal',
      description: 'Create Stripe customer portal session',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        returnUrl: 'https://example.com/account'
      }, null, 2)
    },
    {
      id: 'continents',
      name: 'Get Continents',
      method: 'GET',
      path: '/api/store/markets/continents',
      description: 'Returns list of all continents with market counts',
      enabled: true,
    },
    {
      id: 'countries-by-continent',
      name: 'Get Countries by Continent',
      method: 'GET',
      path: '/api/store/markets/countries/{continentId}',
      description: 'Returns countries within a specific continent',
      enabled: true,
      pathParams: [
        {
          name: 'continentId',
          type: 'string',
          required: true,
          description: 'ID of the continent to get countries for',
          placeholder: 'e.g., 1'
        }
      ]
    },
    {
      id: 'markets-by-country',
      name: 'Get Markets by Country',
      method: 'GET',
      path: '/api/store/markets/{countryId}',
      description: 'Returns markets within a specific country',
      enabled: true,
      pathParams: [
        {
          name: 'countryId',
          type: 'string',
          required: true,
          description: 'ID of the country to get markets for',
          placeholder: 'e.g., us'
        }
      ]
    },
    {
      id: 'search-markets',
      name: 'Search Markets',
      method: 'GET',
      path: '/api/store/markets/search',
      description: 'Search markets by name with optional filtering',
      enabled: true,
      queryParams: [
        {
          name: 'query',
          type: 'string',
          required: true,
          description: 'Search query (minimum 2 characters)',
          placeholder: 'e.g., New York'
        },
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Maximum number of results (default: 50, max: 100)',
          placeholder: 'e.g., 10'
        },
        {
          name: 'category',
          type: 'string',
          required: false,
          description: 'Filter by category: Urban, Resort, Entertainment, etc.',
          placeholder: 'e.g., Urban'
        }
      ]
    },
    {
      id: 'market-details',
      name: 'Get Market Details',
      method: 'GET',
      path: '/api/store/markets/{marketId}/details',
      description: 'Returns detailed market information and pricing',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market to get details for',
          placeholder: 'e.g., nyc'
        }
      ]
    },
    {
      id: 'stripe-health',
      name: 'Stripe Health Check',
      method: 'GET',
      path: '/api/store/stripe/health',
      description: 'Returns Stripe configuration and health status (no auth required)',
      enabled: true,
    },
    {
      id: 'subscription-plans',
      name: 'Get Subscription Plans',
      method: 'GET',
      path: '/api/store/subscriptions/plans',
      description: 'List all available subscription plans with optional filtering',
      enabled: true,
      queryParams: [
        {
          name: 'type',
          type: 'string',
          required: false,
          description: 'Filter by type: market, country, continent',
          placeholder: 'e.g., market'
        },
        {
          name: 'marketId',
          type: 'string',
          required: false,
          description: 'Filter by specific market ID',
          placeholder: 'e.g., 123'
        },
        {
          name: 'active',
          type: 'boolean',
          required: false,
          description: 'Filter by active status (default: true)',
          placeholder: 'true or false'
        }
      ]
    },
    {
      id: 'subscription-purchase',
      name: 'Subscription Purchase',
      method: 'POST',
      path: '/api/store/subscriptions/purchase',
      description: 'Create a subscription purchase session',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        planId: 1
      }, null, 2)
    },
    {
      id: 'stripe-subscription-webhook',
      name: 'Stripe Subscription Webhook',
      method: 'POST',
      path: '/api/store/stripe/subscription-webhook',
      description: 'Stripe subscription webhook endpoint',
      enabled: true,
      requestBodyTemplate: '{}'
    },
  ],
  'marketcompass-admin': [
    {
      id: 'upload-market-report',
      name: 'Upload Market Report',
      method: 'POST',
      path: '/api/marketcompass/admin/markets/{marketId}/reports',
      description: 'Uploads a new market report for a specific market (requires market_compass.admin permission)',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market to upload report for',
          placeholder: 'e.g., 123'
        }
      ],
      requestBodyTemplate: 'Form data with file, reportMonth, shortDescription, mediumDescription, longDescription'
    },
    {
      id: 'update-market-report',
      name: 'Update Market Report',
      method: 'PUT',
      path: '/api/marketcompass/admin/reports/{reportId}/update',
      description: 'Updates metadata for an existing market report (requires market_compass.admin permission)',
      enabled: true,
      pathParams: [
        {
          name: 'reportId',
          type: 'string',
          required: true,
          description: 'ID of the report to update',
          placeholder: 'e.g., 456'
        }
      ],
      requestBodyTemplate: JSON.stringify({
        shortDescription: 'Short description',
        mediumDescription: 'Medium description',
        longDescription: 'Long description',
        isPublished: true
      }, null, 2)
    },
    {
      id: 'delete-market-report',
      name: 'Delete Market Report',
      method: 'DELETE',
      path: '/api/marketcompass/admin/reports/{reportId}',
      description: 'Soft deletes a market report (requires market_compass.admin permission)',
      enabled: true,
      pathParams: [
        {
          name: 'reportId',
          type: 'string',
          required: true,
          description: 'ID of the report to delete',
          placeholder: 'e.g., 456'
        }
      ]
    },
    {
      id: 'upload-market-podcast',
      name: 'Upload Market Podcast',
      method: 'POST',
      path: '/api/marketcompass/admin/markets/{marketId}/podcasts',
      description: 'Uploads a new market podcast (requires market_compass.admin permission)',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'ID of the market to upload podcast for',
          placeholder: 'e.g., 123'
        }
      ],
      requestBodyTemplate: 'Form data with file, reportMonth, title, description, durationSeconds'
    },
    {
      id: 'report-analytics',
      name: 'Get Report Analytics',
      method: 'GET',
      path: '/api/marketcompass/admin/reports/analytics',
      description: 'Returns access analytics for market reports and podcasts (requires market_compass.admin permission)',
      enabled: true,
      queryParams: [
        {
          name: 'startDate',
          type: 'string',
          required: false,
          description: 'Start date for analytics (default: 30 days ago)',
          placeholder: 'e.g., 2024-01-01'
        },
        {
          name: 'endDate',
          type: 'string',
          required: false,
          description: 'End date for analytics (default: today)',
          placeholder: 'e.g., 2024-01-31'
        }
      ]
    },
  ],
  webhooks: [
    {
      id: 'stripe-webhook',
      name: 'Stripe Webhook',
      method: 'POST',
      path: '/api/webhooks/stripe',
      description: 'Main Stripe webhook endpoint',
      enabled: true,
      requestBodyTemplate: '{}'
    },
    {
      id: 'hubspot-webhook',
      name: 'HubSpot Webhook',
      method: 'POST',
      path: '/api/webhooks/hubspot',
      description: 'HubSpot webhook endpoint for real-time updates',
      enabled: true,
      requestBodyTemplate: '{}'
    },
  ],
  'market-analytics': [
    {
      id: 'list-markets',
      name: 'List Markets by Country',
      method: 'GET',
      path: '/api/market-analytics/{countryId}/markets',
      description: 'Lists all available markets for a specific country with pagination and search (requires API key or market_analytics.all permission)',
      enabled: true,
      pathParams: [
        {
          name: 'countryId',
          type: 'string',
          required: true,
          description: 'Country ID (e.g., 196 for United States)',
          placeholder: 'e.g., 196'
        }
      ],
      queryParams: [
        {
          name: 'search',
          type: 'string',
          required: false,
          description: 'Search term for market names',
          placeholder: 'e.g., vegas'
        },
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Number of results (1-100, default: 50)',
          placeholder: 'e.g., 20'
        },
        {
          name: 'offset',
          type: 'number',
          required: false,
          description: 'Pagination offset (default: 0)',
          placeholder: 'e.g., 0'
        }
      ]
    },
    {
      id: 'market-performance',
      name: 'Market Performance',
      method: 'GET',
      path: '/api/market-analytics/markets/{marketId}/performance',
      description: 'Get comprehensive performance metrics for a specific market (requires API key or market_analytics.all permission)',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'Market ID (e.g., 620 for Las Vegas)',
          placeholder: 'e.g., 620'
        }
      ],
      queryParams: [
        {
          name: 'period',
          type: 'string',
          required: false,
          description: 'Time period: current, 3_month, 12_month, ytd',
          placeholder: 'e.g., current'
        },
        {
          name: 'property_type',
          type: 'string',
          required: false,
          description: 'Filter by hotel category: Luxury, Upper Upscale, Upscale, Upper Midscale, Midscale, Economy, or All (default: All)',
          placeholder: 'e.g., Luxury'
        },
        {
          name: 'forecast_scenario',
          type: 'string',
          required: false,
          description: 'Filter by forecast scenario',
          placeholder: 'e.g., baseline'
        }
      ]
    },
    {
      id: 'compare-markets',
      name: 'Compare Markets',
      method: 'GET',
      path: '/api/market-analytics/markets/compare',
      description: 'Compare multiple markets across selected metrics (requires API key or market_analytics.all permission)',
      enabled: true,
      queryParams: [
        {
          name: 'market_ids',
          type: 'string',
          required: true,
          description: 'Comma-separated market IDs (2-10 markets)',
          placeholder: 'e.g., 620,634,625'
        },
        {
          name: 'metrics',
          type: 'string',
          required: true,
          description: 'Comma-separated metrics: adr,revpar,occupancy,demand,supply,revenue',
          placeholder: 'e.g., adr,occupancy,revpar'
        },
        {
          name: 'period',
          type: 'string',
          required: false,
          description: 'Time period (default: current)',
          placeholder: 'e.g., 12_month'
        }
      ]
    },
    {
      id: 'market-rankings',
      name: 'Market Rankings',
      method: 'GET',
      path: '/api/market-analytics/{countryId}/markets/rankings',
      description: 'Get top/bottom performing markets within a country by metric (requires API key or market_analytics.all permission)',
      enabled: true,
      pathParams: [
        {
          name: 'countryId',
          type: 'string',
          required: true,
          description: 'Country ID (e.g., 196 for United States)',
          placeholder: 'e.g., 196'
        }
      ],
      queryParams: [
        {
          name: 'metric',
          type: 'string',
          required: true,
          description: 'Metric: adr, revpar, occupancy, demand_growth, revenue_growth, supply, demand',
          placeholder: 'e.g., revpar'
        },
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Number of results (1-50, default: 10)',
          placeholder: 'e.g., 10'
        },
        {
          name: 'order',
          type: 'string',
          required: false,
          description: 'Sort order: asc or desc (default: desc)',
          placeholder: 'e.g., desc'
        },
        {
          name: 'period',
          type: 'string',
          required: false,
          description: 'Time period (default: current)',
          placeholder: 'e.g., ytd'
        }
      ]
    },
    {
      id: 'market-timeseries',
      name: 'Market Time Series',
      method: 'GET',
      path: '/api/market-analytics/markets/{marketId}/timeseries',
      description: 'Get historical time series data for trend analysis (requires API key or market_analytics.all permission)',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'Market ID',
          placeholder: 'e.g., 620'
        }
      ],
      queryParams: [
        {
          name: 'start_date',
          type: 'string',
          required: true,
          description: 'Start date (YYYY-MM-DD)',
          placeholder: 'e.g., 2024-01-01'
        },
        {
          name: 'end_date',
          type: 'string',
          required: true,
          description: 'End date (YYYY-MM-DD)',
          placeholder: 'e.g., 2024-12-31'
        },
        {
          name: 'metrics',
          type: 'string',
          required: true,
          description: 'Comma-separated metrics',
          placeholder: 'e.g., adr,occupancy,revpar'
        },
        {
          name: 'property_type',
          type: 'string',
          required: false,
          description: 'Filter by hotel category: Luxury, Upper Upscale, Upscale, Upper Midscale, Midscale, Economy, or All (default: All)',
          placeholder: 'e.g., Luxury'
        }
      ]
    },
    {
      id: 'supply-demand',
      name: 'Supply & Demand Analysis',
      method: 'GET',
      path: '/api/market-analytics/markets/{marketId}/supply-demand',
      description: 'Get supply and demand metrics including construction pipeline (requires API key or market_analytics.all permission)',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'Market ID',
          placeholder: 'e.g., 620'
        }
      ],
      queryParams: [
        {
          name: 'period',
          type: 'string',
          required: false,
          description: 'Time period (default: current)',
          placeholder: 'e.g., 12_month'
        }
      ]
    },
    {
      id: 'country-summary',
      name: 'Country Market Summary',
      method: 'GET',
      path: '/api/market-analytics/{countryId}/summary',
      description: 'Get aggregated metrics for a specific country (requires API key or market_analytics.all permission)',
      enabled: true,
      pathParams: [
        {
          name: 'countryId',
          type: 'string',
          required: true,
          description: 'Country ID (e.g., 196 for United States)',
          placeholder: 'e.g., 196'
        }
      ],
      queryParams: [
        {
          name: 'period',
          type: 'string',
          required: false,
          description: 'Time period (default: current)',
          placeholder: 'e.g., ytd'
        },
        {
          name: 'property_type',
          type: 'string',
          required: false,
          description: 'Filter by hotel category: Luxury, Upper Upscale, Upscale, Upper Midscale, Midscale, Economy, or All (default: All)',
          placeholder: 'e.g., Luxury'
        },
        {
          name: 'aggregation',
          type: 'string',
          required: false,
          description: 'Aggregation method: avg, sum, weighted_avg (default: avg)',
          placeholder: 'e.g., weighted_avg'
        }
      ]
    },
    {
      id: 'market-development',
      name: 'Market Development',
      method: 'GET',
      path: '/api/market-analytics/markets/{marketId}/development',
      description: 'Get construction, transaction, and valuation data (requires API key or market_analytics.all permission)',
      enabled: true,
      pathParams: [
        {
          name: 'marketId',
          type: 'string',
          required: true,
          description: 'Market ID',
          placeholder: 'e.g., 620'
        }
      ]
    }
  ],
  hubspot: [
    // Deals
    {
      id: 'create-deal',
      name: 'Create Deal',
      method: 'POST',
      path: '/api/hubspot/deals',
      description: 'Creates a new deal in HubSpot (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        properties: {
          dealname: 'Test Deal ' + new Date().toISOString(),
          amount: '50000',
          dealstage: HubSpotDealStage.LEAD_GENERATED, // Uses the actual ID: 962213552
          closedate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
        }
      }, null, 2)
    },
    {
      id: 'get-deal',
      name: 'Get Deal',
      method: 'GET',
      path: '/api/hubspot/deals/{dealId}',
      description: 'Gets a specific deal by ID (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'dealId',
          type: 'string',
          required: true,
          description: 'HubSpot Deal ID',
          placeholder: 'e.g., 12345'
        }
      ],
      queryParams: [
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., dealname,amount,htl_market_id or *'
        }
      ]
    },
    {
      id: 'update-deal',
      name: 'Update Deal',
      method: 'PUT',
      path: '/api/hubspot/deals/{dealId}',
      description: 'Updates an existing deal (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'dealId',
          type: 'string',
          required: true,
          description: 'HubSpot Deal ID',
          placeholder: 'e.g., 12345'
        }
      ],
      requestBodyTemplate: JSON.stringify({
        properties: {
          dealstage: HubSpotDealStage.IN_DISCUSSIONS, // Uses the actual ID: 985365664
          amount: '75000'
        }
      }, null, 2)
    },
    {
      id: 'delete-deal',
      name: 'Delete Deal',
      method: 'DELETE',
      path: '/api/hubspot/deals/{dealId}',
      description: 'Deletes a deal (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'dealId',
          type: 'string',
          required: true,
          description: 'HubSpot Deal ID',
          placeholder: 'e.g., 12345'
        }
      ]
    },
    {
      id: 'list-deals',
      name: 'List Deals',
      method: 'GET',
      path: '/api/hubspot/deals',
      description: 'Lists all deals with pagination (requires hubspot.all permission or API key)',
      enabled: true,
      queryParams: [
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Number of results per page (max: 100)',
          placeholder: 'e.g., 20'
        },
        {
          name: 'after',
          type: 'string',
          required: false,
          description: 'Pagination cursor',
          placeholder: 'e.g., cursor_value'
        },
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., dealname,amount or * or *,customProp'
        },
        {
          name: 'archived',
          type: 'boolean',
          required: false,
          description: 'Include archived deals',
          placeholder: 'true or false'
        }
      ]
    },
    {
      id: 'search-deals',
      name: 'Search Deals',
      method: 'POST',
      path: '/api/hubspot/deals/search',
      description: 'Search deals with filters and sorting (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        // Test with empty search first
        limit: 10,
        properties: ['*'] // Use * for all properties, or specify: ['dealname', 'amount', 'dealstage', 'closedate']
      }, null, 2)
    },
    {
      id: 'search-deals-filtered',
      name: 'Search Deals (With Filter)',
      method: 'POST',
      path: '/api/hubspot/deals/search',
      description: 'Search deals with standard HubSpot property filters',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        filters: [
          {
            propertyName: 'dealstage',
            operator: 'EQ',
            value: HubSpotDealStage.CONTRACT_SENT // Uses the actual ID: 962003457
          }
        ],
        sorts: [
          {
            propertyName: 'createdate',
            direction: 'DESCENDING'
          }
        ],
        properties: ['dealname', 'amount', 'dealstage', 'createdate', 'closedate'],
        limit: 10
      }, null, 2)
    },
    {
      id: 'search-deals-retainer',
      name: 'Search Deals (Retainer Paid)',
      method: 'POST',
      path: '/api/hubspot/deals/search',
      description: 'Search deals in "Retainer Paid" stage',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        filters: [
          {
            propertyName: 'dealstage',
            operator: 'EQ',
            value: HubSpotDealStage.RETAINER_PAID // Uses the actual ID: 990575212
          }
        ],
        sorts: [
          {
            propertyName: 'amount',
            direction: 'DESCENDING'
          }
        ],
        properties: ['dealname', 'amount', 'dealstage', 'createdate', 'closedate'],
        limit: 10
      }, null, 2)
    },
    // Companies
    {
      id: 'create-company',
      name: 'Create Company',
      method: 'POST',
      path: '/api/hubspot/companies',
      description: 'Creates a new company in HubSpot (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        properties: {
          name: 'Test Company ' + new Date().getTime(),
          domain: 'example.com',
          industry: 'Hospitality',
          numberofemployees: '100',
          annualrevenue: '1000000'
        }
      }, null, 2)
    },
    {
      id: 'get-company',
      name: 'Get Company',
      method: 'GET',
      path: '/api/hubspot/companies/{companyId}',
      description: 'Gets a specific company by ID (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'companyId',
          type: 'string',
          required: true,
          description: 'HubSpot Company ID',
          placeholder: 'e.g., 67890'
        }
      ],
      queryParams: [
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., name,domain,htl_company_type or *'
        }
      ]
    },
    {
      id: 'update-company',
      name: 'Update Company',
      method: 'PUT',
      path: '/api/hubspot/companies/{companyId}',
      description: 'Updates an existing company (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'companyId',
          type: 'string',
          required: true,
          description: 'HubSpot Company ID',
          placeholder: 'e.g., 67890'
        }
      ],
      requestBodyTemplate: JSON.stringify({
        properties: {
          lifecyclestage: 'customer',
          numberofemployees: '1000'
        }
      }, null, 2)
    },
    {
      id: 'delete-company',
      name: 'Delete Company',
      method: 'DELETE',
      path: '/api/hubspot/companies/{companyId}',
      description: 'Deletes a company (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'companyId',
          type: 'string',
          required: true,
          description: 'HubSpot Company ID',
          placeholder: 'e.g., 67890'
        }
      ]
    },
    {
      id: 'list-companies',
      name: 'List Companies',
      method: 'GET',
      path: '/api/hubspot/companies',
      description: 'Lists all companies with pagination (requires hubspot.all permission or API key)',
      enabled: true,
      queryParams: [
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Number of results per page (max: 100)',
          placeholder: 'e.g., 20'
        },
        {
          name: 'after',
          type: 'string',
          required: false,
          description: 'Pagination cursor',
          placeholder: 'e.g., cursor_value'
        },
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., name,domain or * or *,customProp'
        },
        {
          name: 'archived',
          type: 'boolean',
          required: false,
          description: 'Include archived companies',
          placeholder: 'true or false'
        }
      ]
    },
    {
      id: 'search-companies',
      name: 'Search Companies',
      method: 'POST',
      path: '/api/hubspot/companies/search',
      description: 'Search companies with filters and sorting (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        filters: [
          {
            propertyName: 'industry',
            operator: 'EQ',
            value: 'Hospitality'
          }
        ],
        sorts: [
          {
            propertyName: 'name',
            direction: 'ASCENDING'
          }
        ],
        properties: ['*'], // Use * for all properties, or specify: ['name', 'domain', 'industry', 'numberofemployees'],
        limit: 10
      }, null, 2)
    },
    // Contacts
    {
      id: 'create-contact',
      name: 'Create Contact',
      method: 'POST',
      path: '/api/hubspot/contacts',
      description: 'Creates a new contact in HubSpot (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        properties: {
          email: 'test' + new Date().getTime() + '@example.com',
          firstname: 'John',
          lastname: 'Doe',
          jobtitle: 'Revenue Manager',
          company: 'Test Hotel Group'
        }
      }, null, 2)
    },
    {
      id: 'get-contact',
      name: 'Get Contact',
      method: 'GET',
      path: '/api/hubspot/contacts/{contactId}',
      description: 'Gets a specific contact by ID (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'contactId',
          type: 'string',
          required: true,
          description: 'HubSpot Contact ID',
          placeholder: 'e.g., 11111'
        }
      ],
      queryParams: [
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., email,firstname,lastname,htl_role or *'
        }
      ]
    },
    {
      id: 'update-contact',
      name: 'Update Contact',
      method: 'PUT',
      path: '/api/hubspot/contacts/{contactId}',
      description: 'Updates an existing contact (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'contactId',
          type: 'string',
          required: true,
          description: 'HubSpot Contact ID',
          placeholder: 'e.g., 11111'
        }
      ],
      requestBodyTemplate: JSON.stringify({
        properties: {
          lifecyclestage: 'customer',
          phone: '+1 555-1234'
        }
      }, null, 2)
    },
    {
      id: 'delete-contact',
      name: 'Delete Contact',
      method: 'DELETE',
      path: '/api/hubspot/contacts/{contactId}',
      description: 'Deletes a contact (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'contactId',
          type: 'string',
          required: true,
          description: 'HubSpot Contact ID',
          placeholder: 'e.g., 11111'
        }
      ]
    },
    {
      id: 'list-contacts',
      name: 'List Contacts',
      method: 'GET',
      path: '/api/hubspot/contacts',
      description: 'Lists all contacts with pagination (requires hubspot.all permission or API key)',
      enabled: true,
      queryParams: [
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Number of results per page (max: 100)',
          placeholder: 'e.g., 20'
        },
        {
          name: 'after',
          type: 'string',
          required: false,
          description: 'Pagination cursor',
          placeholder: 'e.g., cursor_value'
        },
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., email,firstname,lastname or * or *,customProp'
        },
        {
          name: 'archived',
          type: 'boolean',
          required: false,
          description: 'Include archived contacts',
          placeholder: 'true or false'
        }
      ]
    },
    {
      id: 'search-contacts',
      name: 'Search Contacts',
      method: 'POST',
      path: '/api/hubspot/contacts/search',
      description: 'Search contacts with filters and sorting (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        filters: [
          {
            propertyName: 'jobtitle',
            operator: 'CONTAINS_TOKEN',
            value: 'Manager'
          }
        ],
        sorts: [
          {
            propertyName: 'lastname',
            direction: 'ASCENDING'
          }
        ],
        properties: ['*'], // Use * for all properties, or specify: ['email', 'firstname', 'lastname', 'jobtitle', 'company'],
        limit: 10
      }, null, 2)
    },
    // Listings (HubSpot Object Library custom object)
    {
      id: 'create-listing',
      name: 'Create Listing',
      method: 'POST',
      path: '/api/hubspot/listings',
      description: 'Creates a new listing (hotel property) in HubSpot (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        properties: {
          name: 'Test Hotel ' + new Date().getTime(),
          address: '123 Main Street',
          city: 'Orlando',
          state: 'FL',
          zip: '32801'
        }
      }, null, 2)
    },
    {
      id: 'get-listing',
      name: 'Get Listing',
      method: 'GET',
      path: '/api/hubspot/listings/{listingId}',
      description: 'Gets a specific listing by ID (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'listingId',
          type: 'string',
          required: true,
          description: 'HubSpot Listing ID',
          placeholder: 'e.g., 99999'
        }
      ],
      queryParams: [
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., htl_property_name,rooms,spa or *'
        }
      ]
    },
    {
      id: 'update-listing',
      name: 'Update Listing',
      method: 'PUT',
      path: '/api/hubspot/listings/{listingId}',
      description: 'Updates an existing listing (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'listingId',
          type: 'string',
          required: true,
          description: 'HubSpot Listing ID',
          placeholder: 'e.g., 99999'
        }
      ],
      requestBodyTemplate: JSON.stringify({
        properties: {
          city: 'Miami',
          state: 'FL'
        }
      }, null, 2)
    },
    {
      id: 'delete-listing',
      name: 'Delete Listing',
      method: 'DELETE',
      path: '/api/hubspot/listings/{listingId}',
      description: 'Deletes a listing (requires hubspot.all permission or API key)',
      enabled: true,
      pathParams: [
        {
          name: 'listingId',
          type: 'string',
          required: true,
          description: 'HubSpot Listing ID',
          placeholder: 'e.g., 99999'
        }
      ]
    },
    {
      id: 'list-listings',
      name: 'List Listings',
      method: 'GET',
      path: '/api/hubspot/listings',
      description: 'Lists all listings with pagination (requires hubspot.all permission or API key)',
      enabled: true,
      queryParams: [
        {
          name: 'limit',
          type: 'number',
          required: false,
          description: 'Number of results per page (max: 100)',
          placeholder: 'e.g., 20'
        },
        {
          name: 'after',
          type: 'string',
          required: false,
          description: 'Pagination cursor',
          placeholder: 'e.g., cursor_value'
        },
        {
          name: 'properties',
          type: 'string',
          required: false,
          description: 'Comma-separated list of properties to return. Use * for all properties',
          placeholder: 'e.g., htl_property_name,rooms or * or *,customProp'
        },
        {
          name: 'archived',
          type: 'boolean',
          required: false,
          description: 'Include archived listings',
          placeholder: 'true or false'
        }
      ]
    },
    {
      id: 'search-listings',
      name: 'Search Listings',
      method: 'POST',
      path: '/api/hubspot/listings/search',
      description: 'Search listings with filters and sorting (requires hubspot.all permission or API key)',
      enabled: true,
      requestBodyTemplate: JSON.stringify({
        // Simple search without filters to test basic functionality
        limit: 10,
        properties: ['*'] // Use * for all properties, or specify: ['name', 'city', 'state']
      }, null, 2)
    },
    {
      id: 'get-deal-pipelines',
      name: 'Get Deal Pipelines & Stages',
      method: 'GET',
      path: '/api/hubspot/pipelines/deals',
      description: 'Retrieves all deal pipelines and their stages with IDs (requires hubspot.all permission or API key)',
      enabled: true
    },
    // Properties endpoints
    {
      id: 'get-deal-properties',
      name: 'Get Deal Properties',
      method: 'GET',
      path: '/api/hubspot/properties/deals',
      description: 'Retrieves all properties (standard + custom) for deals (requires hubspot.all permission or API key)',
      enabled: true
    },
    {
      id: 'get-company-properties',
      name: 'Get Company Properties',
      method: 'GET',
      path: '/api/hubspot/properties/companies',
      description: 'Retrieves all properties (standard + custom) for companies (requires hubspot.all permission or API key)',
      enabled: true
    },
    {
      id: 'get-contact-properties',
      name: 'Get Contact Properties',
      method: 'GET',
      path: '/api/hubspot/properties/contacts',
      description: 'Retrieves all properties (standard + custom) for contacts (requires hubspot.all permission or API key)',
      enabled: true
    },
    {
      id: 'get-listing-properties',
      name: 'Get Listing Properties',
      method: 'GET',
      path: '/api/hubspot/properties/listings',
      description: 'Retrieves all properties (standard + custom) for listings (requires hubspot.all permission or API key)',
      enabled: true
    },
    {
      id: 'get-deal-property-names',
      name: 'Get Deal Property Names',
      method: 'GET',
      path: '/api/hubspot/properties/deals/names',
      description: 'Retrieves just the property names for deals as a string array (requires hubspot.all permission or API key)',
      enabled: true
    },
    {
      id: 'get-company-property-names',
      name: 'Get Company Property Names',
      method: 'GET',
      path: '/api/hubspot/properties/companies/names',
      description: 'Retrieves just the property names for companies as a string array (requires hubspot.all permission or API key)',
      enabled: true
    },
    {
      id: 'get-contact-property-names',
      name: 'Get Contact Property Names',
      method: 'GET',
      path: '/api/hubspot/properties/contacts/names',
      description: 'Retrieves just the property names for contacts as a string array (requires hubspot.all permission or API key)',
      enabled: true
    },
    {
      id: 'get-listing-property-names',
      name: 'Get Listing Property Names',
      method: 'GET',
      path: '/api/hubspot/properties/listings/names',
      description: 'Retrieves just the property names for listings as a string array (requires hubspot.all permission or API key)',
      enabled: true
    }
  ]
};

// Sort endpoints alphabetically within each group
export const API_ENDPOINTS_GROUPED_SORTED: Record<string, ApiEndpoint[]> = Object.entries(API_ENDPOINTS_GROUPED).reduce(
  (acc, [group, endpoints]) => ({
    ...acc,
    [group]: endpoints.sort((a, b) => a.name.localeCompare(b.name))
  }),
  {}
);

// Flatten the grouped endpoints for backward compatibility
export const API_ENDPOINTS: ApiEndpoint[] = Object.values(API_ENDPOINTS_GROUPED_SORTED).flat();